import React from 'react';
import styled, { useTheme } from 'styled-components';

import { Theme } from '../../../core';
import Icon, { IconType } from '../Icon';
import Link from '../Link';
import { TextProps } from '../Text';

export interface LinkButtonProps {
  children: React.ReactNode;
  color?: string;
  disabled?: boolean;
  iconBackgroundColor?: string;
  iconColor?: string;
  iconLeft?: IconType;
  iconRight?: IconType;
  iconSize?: number;
  onClick: (e: any) => void;
  size?: 'xsmall' | 'small' | 'smallmedium' | 'medium' | 'large';
  minWidth?: string;
  className?: string;
}

const StyledLinkButton = styled(Link).attrs({
  as: 'button',
  type: 'button',
})<{ disabled: boolean; color?: string; size?: string; minWidth?: string }>`
  -webkit-appearance: none;
  background-color: transparent;
  border: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  font-weight: ${({ theme }) => theme.linkButtonFontWeight || 400};
  font-family: ${({ theme }) => theme.buttonFont};
  text-align: center;
  justify-content: center;

  ${({ color }) =>
    color &&
    `
    color: ${color};
  `}

  ${({ size, theme, minWidth }) =>
    theme.name === Theme.theme3 &&
    (size === 'xsmall'
      ? `
        height: ${theme.buttonHeight.xsmall};
        font-size: ${theme.buttonFontSize.xsmall};
        min-width: ${minWidth || '110px'};
        `
      : size === 'small'
      ? `
        height: ${theme.buttonHeight.small};
        font-size: ${theme.buttonFontSize.small};
        min-width: ${minWidth || '150px'};
          `
      : size === 'smallmedium'
      ? `
        height: ${theme.buttonHeight.smallmedium};
        font-size: ${theme.buttonFontSize.smallmedium};
        min-width: ${minWidth || '190px'};
        `
      : size === 'medium'
      ? `
        height: ${theme.buttonHeight.medium};
        font-size: ${theme.buttonFontSize.medium};
        min-width: ${minWidth || '190px'};
        `
      : size === 'large'
      ? `
        height: ${theme.buttonHeight.large};
        font-size: ${theme.buttonFontSize.large};
        min-width: ${minWidth || '300px'};
        `
      : '')}
`;

const StyledIconLeft = styled(Icon)<{ disabled: boolean }>`
  margin-right: 4px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

const StyledIconRight = styled(Icon)<{ disabled: boolean }>`
  margin-left: 4px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

const LinkButton = ({
  children,
  color,
  disabled = false,
  iconBackgroundColor,
  iconColor,
  iconLeft,
  iconRight,
  iconSize,
  onClick,
  size = 'medium',
  align,
  weight,
  noMargin,
  lineHeight,
  minWidth,
  className,
}: LinkButtonProps & TextProps) => {
  const theme = useTheme();

  const defaultIconColor = iconColor ? iconColor : theme.linkButtonIconColor;

  return (
    <StyledLinkButton
      size={size}
      align={align}
      weight={weight}
      noMargin={noMargin}
      lineHeight={lineHeight}
      minWidth={minWidth}
      disabled={disabled}
      color={color}
      onClick={onClick}
      className={className}
    >
      {iconLeft ? (
        <StyledIconLeft
          type={iconLeft}
          size={iconSize || theme.linkButtonIconSize[size]}
          color={disabled ? theme.linkButtonIconColorDisabled : defaultIconColor}
          backgroundColor={iconBackgroundColor}
          disabled={disabled}
        />
      ) : null}
      {children}
      {iconRight ? (
        <StyledIconRight
          type={iconRight}
          size={iconSize || theme.linkButtonIconSize[size]}
          color={disabled ? theme.linkButtonIconColorDisabled : defaultIconColor}
          backgroundColor={iconBackgroundColor}
          disabled={disabled}
        />
      ) : null}
    </StyledLinkButton>
  );
};

export default LinkButton;
